import { SeatDistribution } from "../enums";

type GenerateOptions = {
  start?: number;
  count: number;
  reverse?: boolean;
  type: SeatDistribution;
};

export const selectSeatDistribution = (
  value: SeatDistribution,
  seatsQuantity: number,
  startsAt: number = 1,
  reverse: boolean = false
) => {
  return generateSeatLabels({
    start: startsAt,
    count: seatsQuantity,
    reverse,
    type: value,
  });
};

const generateSeatLabels = ({
  start = 1,
  count,
  reverse = false,
  type,
}: GenerateOptions): string[] => {
  let result: string[] = [];
  switch (type) {
    case SeatDistribution.NORMAL:
      result = Array.from({ length: count }, (_, i) => String(start + i));
      break;

    case SeatDistribution.ODDS:
      result = Array.from({ length: count }, (_, i) => String(start + i * 2));
      break;

    case SeatDistribution.EVENS:
      result = Array.from({ length: count }, (_, i) => String(start + 1 + i * 2));
      break;

    case SeatDistribution.ODDS_TO_EVENS: {
      const odds = Array.from({ length: Math.ceil(count / 2) }, (_, i) => String(start + i * 2));
      const evens = Array.from({ length: Math.floor(count / 2) }, (_, i) => String(start + 1 + i * 2)).reverse();
      result = [...odds, ...evens];
      break;
    }

    case SeatDistribution.CENTER_ODDS_TO_EVENS: {
      const left = [];
      const right = [];

      for (let i = 0; i < count; i++) {
        const value = start + i;
        if (value % 2 !== 0) {
          left.unshift(String(value));
        } else {
          right.push(String(value));
        }
      }

      result = [...left, ...right];
      break;
    }

    case SeatDistribution.UPPER_CASE:
      result = generateLetters(start, count, true);
      break;

    case SeatDistribution.LOWER_CASE:
      result = generateLetters(start, count, false);
      break;

    default:
      return [];
  }
  return reverse ? result.reverse() : result;
};

const generateLetters = (
  start: number,
  count: number,
  upperCase: boolean
): string[] => {
  const letters: string[] = [];

  for (let i = 0; i < count; i++) {
    const index = start - 1 + i;
    letters.push(numberToLetters(index, upperCase));
  }

  return letters;
};

const numberToLetters = (num: number, upperCase: boolean): string => {
  const base = 26;
  const safeNum = num < 0 ? 0 : num;
  const letterIndex = safeNum % base;
  const repeat = Math.floor(safeNum / base) + 1;

  const result = String.fromCharCode(letterIndex + 65).repeat(repeat);

  return upperCase ? result : result.toLowerCase();
};

export const isLetterDistribution = (type: SeatDistribution): boolean =>
  type === SeatDistribution.UPPER_CASE || type === SeatDistribution.LOWER_CASE;

export const startsAtToDisplayNumber = (
  type: SeatDistribution,
  startsAt: number
): number => {
  const value = Math.max(1, Math.floor(startsAt || 1));

  switch (type) {
    case SeatDistribution.EVENS:
      // Util produces first label = start + 1, so the first even shown is that.
      return value + 1;
    case SeatDistribution.ODDS:
    default:
      return value;
  }
};

export const displayNumberToStartsAt = (
  type: SeatDistribution,
  display: number
): number => {
  const value = Math.max(1, Math.floor(display || 1));

  switch (type) {
    case SeatDistribution.ODDS: {
      // Force an odd display value; util uses it directly as the first odd.
      const odd = value % 2 === 0 ? value + 1 : value;
      return odd;
    }
    case SeatDistribution.EVENS: {
      // Force an even display value; util adds 1 internally, so store even - 1.
      const even = value % 2 === 0 ? value : value + 1;
      return even - 1;
    }
    default:
      return value;
  }
};

export const startsAtToLetter = (
  startsAt: number,
  upperCase: boolean = true
): string => {
  const index = Math.max(1, Math.floor(startsAt || 1)) - 1;
  return numberToLetters(index, upperCase);
};

export const letterToStartsAt = (letters: string): number => {
  if (!letters) return 1;

  const clean = letters.replace(/[^a-zA-Z]/g, "").toUpperCase();
  if (!clean) return 1;

  const letterIndex = clean.charCodeAt(0) - 65;
  const repeat = clean.length;

  return letterIndex + (repeat - 1) * 26 + 1;
};